import React, { useEffect, useState } from "react";
import { Box, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import useApi from "../../hooks/useGaneshApi";
import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import AnalysisJobDetailsModal from "./AnalysisJobDetailsModal";
import {
  getDateValue,
  renderHumanReadableDateCell,
  renderNumberCell,
} from "utils/dataGrid";
import { DatasetType, ExportType } from "../../types/Compliance";

interface SummaryRow {
  admin_id: number;
  admin_name: string;
  count_queue: number;
  count_in_progress: number;
  count_complete: number;
  count_failed: number;
  last_analysis_report_date: string;
}

interface Props {
  datasetType: string;
  intervalDays: number;
  exportType: string;
  setDatasetType: (value: DatasetType) => void;
  setIntervalDays: (value: number) => void;
  setExportType: (value: ExportType) => void;
}

const ExportJobsLogTable: React.FC<Props> = ({
  datasetType,
  intervalDays,
  exportType,
  setDatasetType,
  setIntervalDays,
  setExportType,
}) => {
  const [summaryData, setSummaryData] = useState<SummaryRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openAdminId, setOpenAdminId] = useState<string | number | null>(null);
  const { apiRequest } = useApi();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await apiRequest<{ data: SummaryRow[] }>(
          `/v1/export-jobs/status?dataset_type=${datasetType}&interval_days=${intervalDays}&export_type=${exportType}`
        );
        if (response.data) {
          // Handle the new success_response format
          const data = response.data.data || response.data;
          setSummaryData(data);
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [datasetType, intervalDays, exportType]);

  const columns_def: GridColDef[] = [
    {
      field: "admin_id",
      headerName: "Admin ID",
      width: 100,
      type: "number",
      renderCell: renderNumberCell,
    },
    {
      field: "admin_name",
      headerName: "Admin Name",
      width: 180,
      type: "string",
    },
    {
      field: "count_queue",
      headerName: "Queued",
      width: 100,
      type: "number",
      renderCell: renderNumberCell,
    },
    {
      field: "count_in_progress",
      headerName: "In Progress",
      width: 130,
      type: "number",
      renderCell: renderNumberCell,
    },
    {
      field: "count_complete",
      headerName: "Complete",
      width: 100,
      type: "number",
      renderCell: renderNumberCell,
    },
    {
      field: "count_failed",
      headerName: "Failed",
      width: 100,
      type: "number",
      renderCell: renderNumberCell,
    },
    {
      field: "last_analysis_report_date",
      headerName: "Last Run",
      width: 250,
      type: "date",
      valueGetter: getDateValue,
      renderCell: renderHumanReadableDateCell,
    },
    {
      field: "actions",
      headerName: "Jobs",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <AnalysisJobDetailsModal
            adminId={row.admin_id}
            datasetType={datasetType}
            intervalDays={intervalDays}
            exportType={exportType}
            label="Jobs"
            isOpen={openAdminId === row.admin_id}
            onOpen={() => setOpenAdminId(row.admin_id)}
            onClose={() => setOpenAdminId(null)}
            endpoint="default"
          />
        </Box>
      ),
    },
    {
      field: "failed_jobs",
      headerName: "Failed Jobs",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <AnalysisJobDetailsModal
            adminId={row.admin_id}
            datasetType={datasetType}
            intervalDays={intervalDays}
            exportType={exportType}
            label="Failed Jobs"
            isOpen={openAdminId === `failed-${row.admin_id}`}
            onOpen={() => setOpenAdminId(`failed-${row.admin_id}`)}
            onClose={() => setOpenAdminId(null)}
            endpoint="failed"
          />
        </Box>
      ),
    },
  ];

  const defaultGridState: GridInitialState = {
    filter: {
      filterModel: {
        items: [],
      },
    },
    sorting: { sortModel: [] },
    pagination: { paginationModel: { pageSize: 25, page: 0 } },
    pinnedColumns: { left: [] },
    columns: { columnVisibilityModel: {} },
    density: "compact",
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={2}>
        <FormControl>
          <InputLabel>Dataset Type</InputLabel>
          <Select
            value={datasetType}
            onChange={(e) => setDatasetType(e.target.value as DatasetType)}
          >
            <MenuItem value="delivery">Delivery</MenuItem>
            <MenuItem value="volume_upgrade">Volume Upgrade</MenuItem>
            <MenuItem value="sample">Sample</MenuItem>
            <MenuItem value="state">State</MenuItem>
            <MenuItem value="country">Country</MenuItem>
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Export Type</InputLabel>
          <Select
            value={exportType}
            onChange={(e) => setExportType(e.target.value as ExportType)}
          >
            <MenuItem value="analysis">Analysis</MenuItem>
            <MenuItem value="brand_analysis">Brand Analysis</MenuItem>
            <MenuItem value="brand_discovery">Brand Discovery</MenuItem>
            <MenuItem value="chains">Chains</MenuItem>
            <MenuItem value="duplicates">Duplicates</MenuItem>
            <MenuItem value="place_export">Place Export</MenuItem>
            <MenuItem value="saga_diff">Saga Diff</MenuItem>
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Interval Days</InputLabel>
          <Select
            value={intervalDays}
            onChange={(e) => setIntervalDays(Number(e.target.value))}
          >
            <MenuItem value={7}>7 Days</MenuItem>
            <MenuItem value={14}>14 Days</MenuItem>
            <MenuItem value={30}>30 Days</MenuItem>
            <MenuItem value={90}>90 Days</MenuItem>
            <MenuItem value={180}>180 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <GaneshDataGrid
        data={summaryData}
        loading={isLoading}
        defaultGridState={defaultGridState}
        columns={columns_def}
        gridId={`analysisSummary-${datasetType}`}
        checkboxSelection={false}
      />
    </Box>
  );
};

export default ExportJobsLogTable;
